import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";
import { useIsomorphicLayoutEffect } from "../hooks/useIsomorphicLayoutEffect";
import { AikoTooltip, type AikoTooltipProps } from "./AikoTooltip";
import { TooltipProvider } from "../primitives/Tooltip";

/**
 * `<Truncate>` — text container that truncates at N lines AND auto-renders
 * an `<AikoTooltip>` showing the full text only when overflow is actually
 * present.
 *
 * Drop-in replacement for hand-rolled `truncate` / `line-clamp-{N}` patterns
 * across cards, chips, rows, and meta lines. Removes the need for consumers
 * to plumb an `useIsTruncated` state into a tooltip themselves.
 *
 * Defaults to `lines={1}` (Tailwind `truncate` ellipsis) on a `<span>`. For
 * multi-line clamp, set `lines={2|3|4}`. Polymorphic via `as`.
 *
 * @example Single-line ellipsis
 * ```tsx
 * <Truncate>{customUrl}</Truncate>
 * ```
 *
 * @example Two-line clamp on a paragraph
 * ```tsx
 * <Truncate as="p" lines={2}>{description}</Truncate>
 * ```
 *
 * @example Override the tooltip body for non-string children
 * ```tsx
 * <Truncate tooltipBody={fullName}>
 *   <span className="inline-flex items-center gap-1">
 *     <Icon /> {nick}
 *   </span>
 * </Truncate>
 * ```
 *
 * @remarks
 * - The tooltip auto-enables when the rendered text actually overflows. When
 *   `children` is a non-string ReactNode AND no `tooltipBody` is provided,
 *   the tooltip never mounts (we don't stringify arbitrary nodes).
 * - Requires `<TooltipProvider>` at the app root (per `<AikoTooltip>`).
 * - SSR-safe: ResizeObserver is gated behind `useIsomorphicLayoutEffect`.
 *   The server-rendered markup is just the bare element with truncation
 *   classes; the tooltip wrap evaluates after first client measurement.
 *   The wrapper stays mounted once a tooltip body is available, with
 *   `open={false}` when not truncated, so transient resizes don't remount
 *   the trigger.
 * - For 1-line truncation to take effect, the parent should be a flex
 *   container with `min-w-0` or any block parent with a constrained width.
 *   Same constraint as raw `truncate`. Multi-line clamp needs no parent
 *   constraint.
 *
 * See `<AikoTooltip>` for the underlying tooltip surface and DESIGN.md
 * "Voice" for the AIKO copy rules that apply to any user-visible
 * `tooltipBody` overrides.
 */
export const truncateVariants = cva("min-w-0", {
  variants: {
    lines: {
      1: "truncate",
      2: "line-clamp-2",
      3: "line-clamp-3",
      4: "line-clamp-4",
    },
  },
  defaultVariants: {
    lines: 1,
  },
});

export interface TruncateProps
  extends
    Omit<React.HTMLAttributes<HTMLElement>, "children">,
    VariantProps<typeof truncateVariants> {
  /** Underlying tag. Default `"span"`. */
  as?: keyof JSX.IntrinsicElements;
  /** Disable the tooltip even when text overflows. Default `false`. */
  disableTooltip?: boolean;
  /**
   * Override the tooltip body. Defaults to `children` when it's a string or
   * number. Pass explicitly when children include inline icons / nodes but
   * the tooltip should show only the text.
   */
  tooltipBody?: React.ReactNode;
  /** Tooltip side. Forwarded to `<AikoTooltip>`. Default `"top"`. */
  tooltipSide?: AikoTooltipProps["side"];
  /** Tooltip alignment. Forwarded to `<AikoTooltip>`. Default `"center"`. */
  tooltipAlign?: AikoTooltipProps["align"];
  /** Hover-open delay in ms. Forwarded to `<AikoTooltip>`. */
  tooltipDelayDuration?: AikoTooltipProps["delayDuration"];
  children: React.ReactNode;
}

export const Truncate = React.forwardRef<HTMLElement, TruncateProps>(function Truncate(
  {
    as: Tag = "span",
    lines,
    disableTooltip = false,
    tooltipBody,
    tooltipSide = "top",
    tooltipAlign = "center",
    tooltipDelayDuration,
    className,
    children,
    ...rest
  },
  forwardedRef,
) {
  const localRef = React.useRef<HTMLElement | null>(null);
  const [isTruncated, setIsTruncated] = React.useState(false);
  // Track Radix's hover/focus open state so the tooltip stays in
  // **fully controlled** mode (always-boolean `open`). Declared at the
  // top of the component to satisfy rules-of-hooks (was previously
  // declared after the early-return branch). The earlier
  // `open={!isTruncated ? false : undefined}` flipped between
  // controlled (false) and uncontrolled (undefined) on truncation
  // change, which Radix flagged in the creator-studio production
  // console as "Tooltip is changing from uncontrolled to controlled".
  const [hoverOpen, setHoverOpen] = React.useState(false);

  // Bridge forwarded ref + local ref via a callback ref.
  const setRef = React.useCallback(
    (node: HTMLElement | null) => {
      localRef.current = node;
      if (typeof forwardedRef === "function") {
        forwardedRef(node);
      } else if (forwardedRef) {
        (forwardedRef as React.MutableRefObject<HTMLElement | null>).current = node;
      }
    },
    [forwardedRef],
  );

  useIsomorphicLayoutEffect(() => {
    const el = localRef.current;
    if (!el) return;
    const isMultiLine = lines !== undefined && lines !== null && lines >= 2;
    const measure = () => {
      const overflows = isMultiLine
        ? el.scrollHeight > el.clientHeight
        : el.scrollWidth > el.clientWidth;
      setIsTruncated((prev) => (prev === overflows ? prev : overflows));
    };
    measure();
    // ResizeObserver fires on box-size change; the `children` dep covers
    // text-content changes that don't change box size on their own.
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [lines, children]);

  const Element = Tag as React.ElementType;
  const truncated = (
    <Element ref={setRef} className={cn(truncateVariants({ lines }), className)} {...rest}>
      {children}
    </Element>
  );

  // Resolve the tooltip body. Default to children when it's a primitive
  // (string / number); non-string children require explicit tooltipBody.
  const resolvedBody =
    tooltipBody ?? (typeof children === "string" || typeof children === "number" ? children : null);

  if (disableTooltip || resolvedBody == null || resolvedBody === "") {
    return truncated;
  }

  // Mount the wrapper once we have a body. The local `<TooltipProvider>`
  // makes `<Truncate>` self-contained — it works whether or not the
  // consumer has a global provider. Radix nests providers fine; the
  // closest one wins. Cheap (context-only) so the duplication cost is
  // negligible even at high call-site counts.
  //
  // Tooltip stays in fully-controlled mode (always-boolean `open`); see
  // `hoverOpen` declaration at the top of the component for the why.
  // The wrapper stays mounted across resize-driven truncation flips; the
  // child doesn't remount; tooltip only appears when truncated AND
  // hovered.
  //
  // Overflow tooltips are always label-style (showing the full clipped
  // text), never AIKO comms — pin `tone="minimal"` so the heuristic
  // doesn't promote long truncated strings to the branded paper card.
  // Snappy delay (120ms) is deliberate: users hovering across a list of
  // truncated rows want immediate read-back, not the AikoTooltip default
  // 400ms.
  return (
    <TooltipProvider delayDuration={tooltipDelayDuration ?? 120}>
      <AikoTooltip
        body={resolvedBody}
        tone="minimal"
        side={tooltipSide}
        align={tooltipAlign}
        delayDuration={tooltipDelayDuration ?? 120}
        open={isTruncated && hoverOpen}
        onOpenChange={setHoverOpen}
      >
        {truncated}
      </AikoTooltip>
    </TooltipProvider>
  );
});

Truncate.displayName = "Truncate";
